/**
 * 画像変換ユーティリティ
 * HEIC/HEIF形式の画像をJPEGに変換する
 * 
 * Chrome/Firefox/EdgeはHEIC形式を直接表示できないため、
 * アップロード前にJPEGに変換する必要がある
 */

// HEIC/HEIF形式のMIMEタイプ
const HEIC_TYPES = ['image/heic', 'image/heif'];

/**
 * ファイルがHEIC/HEIF形式かどうかを判定
 * @param {File} file - チェックするファイル
 * @returns {boolean} HEIC/HEIF形式の場合true
 */
export const isHeicFile = (file) => {
  if (!file) return false;
  
  // MIMEタイプでチェック
  if (HEIC_TYPES.includes(file.type?.toLowerCase())) {
    return true;
  }
  
  // 拡張子でチェック（MIMEタイプが空の場合のフォールバック）
  const extension = file.name?.toLowerCase().split('.').pop();
  return extension === 'heic' || extension === 'heif';
};

/**
 * HEIC/HEIF画像をJPEGに変換する
 * @param {File} file - 変換するファイル
 * @param {Object} options - オプション
 * @param {number} options.quality - JPEG品質 (0-1, デフォルト: 0.9)
 * @returns {Promise<File>} 変換後のファイル（HEIC以外はそのまま返す）
 */
export const convertHeicToJpeg = async (file, options = {}) => {
  if (!file) return file;
  
  // HEIC/HEIF以外はそのまま返す
  if (!isHeicFile(file)) {
    return file;
  }
  
  const { quality = 0.9 } = options;
  
  console.log(`[imageConverter] HEIC画像を検出: ${file.name}, 変換中...`);
  
  try {
    const { default: heic2any } = await import('heic2any');
    // HEIC → JPEG変換
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: quality
    });
    
    // Blobが配列で返される場合があるので処理
    const resultBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    // 新しいファイル名を生成（.heic → .jpg）
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    
    // BlobをFileオブジェクトに変換
    const convertedFile = new File([resultBlob], newFileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
    
    console.log(`[imageConverter] HEIC→JPEG変換完了: ${newFileName} (${Math.round(convertedFile.size / 1024)}KB)`);
    
    return convertedFile;
  } catch (error) {
    console.error('[imageConverter] HEIC変換エラー:', error);
    // 変換に失敗した場合はオリジナルを返す
    // サーバー側でも変換を試みるため、完全に失敗するわけではない
    return file;
  }
};

/**
 * 複数のファイルをまとめて変換する
 * @param {File[]} files - 変換するファイルの配列
 * @param {Object} options - オプション
 * @returns {Promise<File[]>} 変換後のファイルの配列
 */
export const convertHeicFilesToJpeg = async (files, options = {}) => {
  if (!files || files.length === 0) return files;
  
  const convertedFiles = await Promise.all(
    files.map(file => convertHeicToJpeg(file, options))
  );
  
  return convertedFiles;
};

export default {
  isHeicFile,
  convertHeicToJpeg,
  convertHeicFilesToJpeg
};
